import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Truck, MapPin, ClipboardList, User, Clock, CheckCircle2, AlertTriangle, Wifi, WifiOff, FileText, Activity, ExternalLink } from 'lucide-react';
import { formatDateTime } from '../lib/format';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet's default marker icon (CRA / webpack can't auto-resolve them).
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = (t) => `mob_track_cache:${t}`;

const STATUS_TONE = {
  pending: 'bg-amber-100 text-amber-800 border-amber-300',
  in_progress: 'bg-blue-100 text-blue-800 border-blue-300',
  completed: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  cancelled: 'bg-slate-200 text-slate-700 border-slate-300',
};

const PublicTracking = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [fromCache, setFromCache] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      // Read from cache first for instant offline-friendly paint
      try {
        const cached = localStorage.getItem(CACHE_KEY(token));
        if (cached) { setData(JSON.parse(cached)); setFromCache(true); setLoading(false); }
      } catch (e) { console.warn('PublicTracking cache read failed:', e?.message || e); }
      try {
        const r = await axios.get(`${BACKEND_URL}/api/public/mobilizations/${token}`);
        setData(r.data);
        setFromCache(false);
        localStorage.setItem(CACHE_KEY(token), JSON.stringify(r.data));
      } catch (e) {
        if (e.response?.status === 404) setError('Mobilization not found. The QR may be invalid or revoked.');
        else if (!navigator.onLine) setError(null); // we'll show cached
        else setError(e.response?.data?.detail || 'Unable to fetch live data.');
      } finally { setLoading(false); }
    };
    fetchData();
    const onOnline = () => { setOnline(true); fetchData(); };
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => { window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline); };
  }, [token]);

  if (loading && !data) return <div className="p-8 text-center text-slate-500">Loading…</div>;
  if (error && !data) return (
    <div className="min-h-screen bg-slate-50 grid place-items-center p-6">
      <Card className="max-w-md w-full"><CardContent className="p-6 text-center">
        <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto" />
        <div className="mt-3 font-semibold">Tracking unavailable</div>
        <div className="text-sm text-slate-500 mt-1">{error}</div>
      </CardContent></Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 pb-12">
      <header className="bg-[hsl(217,91%,24%)] text-white py-5 px-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-blue-200">Live Equipment Tracking</div>
            <h1 className="text-xl font-bold mt-0.5 flex items-center gap-2"><Truck className="h-5 w-5" /> {data?.serial_number || 'Mobilization'}</h1>
          </div>
          <div className="text-right text-xs">
            {online ? (
              <span className="inline-flex items-center gap-1 bg-white/10 px-2 py-1 rounded"><Wifi className="h-3.5 w-3.5" /> Online</span>
            ) : (
              <span className="inline-flex items-center gap-1 bg-amber-500/30 px-2 py-1 rounded"><WifiOff className="h-3.5 w-3.5" /> Offline · Cached</span>
            )}
            {fromCache && online && <div className="mt-1 text-[10px] text-blue-200">Refreshing…</div>}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 -mt-4 space-y-3" data-testid="public-tracking-main">
        {/* Status hero */}
        <Card><CardContent className="p-5 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Current Status</div>
            <Badge className={`mt-1 text-sm px-3 py-1 border ${STATUS_TONE[data?.status] || 'bg-slate-200 text-slate-700'}`} data-testid="public-status">{data?.status?.replace('_',' ').toUpperCase()}</Badge>
            <div className="text-xs text-slate-500 mt-2 inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {data?.current_location_label || data?.destination || '—'}</div>
          </div>
          <div className="text-right text-[10px] text-slate-400">
            <div>Updated</div>
            <div className="text-slate-700 text-xs">{formatDateTime(data?.updated_at || data?.created_at)}</div>
          </div>
        </CardContent></Card>

        {/* Equipment */}
        {data?.equipment && (
          <Card><CardContent className="p-5">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Equipment</div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Info label="Code" value={data.equipment.equipment_code} mono />
              <Info label="Category" value={data.equipment.category} />
              <Info label="Brand / Model" value={[data.equipment.brand, data.equipment.model].filter(Boolean).join(' ')} />
              <Info label="Plate" value={data.equipment.plate_no} mono />
              <Info label="Description" value={data.equipment.description} colSpan={2} />
              <Info label="Vehicle Status" value={data.equipment.status} colSpan={2} />
            </div>
          </CardContent></Card>
        )}

        {/* Live GPS map (vehicle's last known location) */}
        {data?.equipment_location?.lat != null && data?.equipment_location?.lng != null && (
          <Card><CardContent className="p-0 overflow-hidden">
            <div className="px-5 pt-4 pb-2 flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> Live Location</div>
                <div className="text-sm font-semibold text-slate-900 mt-0.5">{data.equipment_location.label || 'Equipment GPS position'}</div>
                <div className="text-[11px] text-slate-500 font-mono mt-0.5">{Number(data.equipment_location.lat).toFixed(5)}, {Number(data.equipment_location.lng).toFixed(5)}</div>
                {data.equipment_location.updated_at && (
                  <div className="text-[10px] text-slate-400 mt-0.5">Updated {formatDateTime(data.equipment_location.updated_at)}</div>
                )}
              </div>
              <a
                href={`https://www.openstreetmap.org/?mlat=${data.equipment_location.lat}&mlon=${data.equipment_location.lng}#map=15/${data.equipment_location.lat}/${data.equipment_location.lng}`}
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[11px] text-[hsl(217,91%,24%)] hover:underline whitespace-nowrap"
                data-testid="public-map-open-osm"
              ><ExternalLink className="h-3 w-3" /> Open in Maps</a>
            </div>
            <div className="h-56 w-full" data-testid="public-tracking-map">
              <MapContainer
                center={[data.equipment_location.lat, data.equipment_location.lng]}
                zoom={14}
                scrollWheelZoom={false}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={[data.equipment_location.lat, data.equipment_location.lng]}>
                  <Popup>
                    <div className="text-xs">
                      <div className="font-semibold">{data?.equipment?.equipment_code || data?.serial_number}</div>
                      <div className="text-slate-500">{data.equipment_location.label || 'Last reported position'}</div>
                    </div>
                  </Popup>
                </Marker>
              </MapContainer>
            </div>
          </CardContent></Card>
        )}

        {/* Project */}
        <Card><CardContent className="p-5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Deployment</div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Info label="Project" value={data?.project_name} colSpan={2} />
            <Info label="From" value={data?.from_location} />
            <Info label="To" value={data?.to_location} />
            <Info label="Sched. Departure" value={data?.scheduled_departure ? formatDateTime(data.scheduled_departure) : '—'} />
            <Info label="Sched. Arrival" value={data?.scheduled_arrival ? formatDateTime(data.scheduled_arrival) : '—'} />
          </div>
        </CardContent></Card>

        {/* Operator */}
        {data?.operator && (
          <Card><CardContent className="p-5">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 inline-flex items-center gap-1"><User className="h-3 w-3" /> Operator</div>
            <div className="text-sm">
              <div className="font-semibold">{data.operator.full_name || '—'}</div>
              <div className="text-xs text-slate-500 mt-0.5">License: <span className="font-mono">{data.operator.license_no || '—'}</span></div>
              <div className="text-xs text-slate-500">Contact: {data.operator.contact_no || '—'}</div>
            </div>
          </CardContent></Card>
        )}

        {/* Signatures */}
        <Card><CardContent className="p-5">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 inline-flex items-center gap-1"><FileText className="h-3 w-3" /> Acknowledgement &amp; Handover</div>
          <ul className="space-y-2 text-sm">
            {(data?.signatures_summary || []).map((s) => (
              <li key={s.role} className="flex items-center justify-between border-l-2 pl-3 py-1" style={{ borderColor: s.signed ? 'rgb(5 150 105)' : 'rgb(203 213 225)' }} data-testid={`public-sig-${s.role}`}>
                <div>
                  <div className="font-semibold capitalize">{s.role}</div>
                  <div className="text-xs text-slate-500">{s.signed ? s.name : <span className="italic text-slate-400">Pending signature</span>}</div>
                </div>
                {s.signed ? (
                  <div className="text-right">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 inline" />
                    <div className="text-[10px] text-slate-500">{formatDateTime(s.signed_at)}</div>
                  </div>
                ) : (
                  <Clock className="h-4 w-4 text-slate-400" />
                )}
              </li>
            ))}
          </ul>
        </CardContent></Card>

        {/* History */}
        {data?.status_history?.length > 0 && (
          <Card><CardContent className="p-5">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 inline-flex items-center gap-1"><Activity className="h-3 w-3" /> Status History</div>
            <ol className="space-y-2 text-sm">
              {data.status_history.slice().reverse().map((h, i) => (
                <li key={i} className="flex items-start gap-3 border-l-2 border-slate-200 pl-3 py-1">
                  <div className="text-[10px] text-slate-400 w-28 flex-shrink-0">{formatDateTime(h.at)}</div>
                  <div className="flex-1">
                    <span className="font-semibold capitalize">{(h.status || '').replace('_',' ')}</span>
                    {h.note && <div className="text-xs text-slate-500">{h.note}</div>}
                    <div className="text-[10px] text-slate-400">by {h.by || 'system'}</div>
                  </div>
                </li>
              ))}
            </ol>
          </CardContent></Card>
        )}

        <div className="text-center text-[11px] text-slate-400 pt-3">
          ESD · PIVI Equipment Solutions · Live tracking via QR code
        </div>
      </main>
    </div>
  );
};

const Info = ({ label, value, mono = false, colSpan = 1 }) => (
  <div className={colSpan === 2 ? 'col-span-2' : ''}>
    <div className="text-[10px] uppercase text-slate-400">{label}</div>
    <div className={`text-sm ${mono ? 'font-mono' : ''}`}>{value || '—'}</div>
  </div>
);

export default PublicTracking;
